import { useEffect, lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import CookieBanner from "@/components/CookieBanner";
import ErrorBoundary from "@/components/ErrorBoundary";
import { isEmailLinkSignIn, completeEmailLinkSignIn } from "@/lib/auth";

// Component to track page views on route changes via gtag (configured in index.html)
const GoogleAnalyticsTracker = () => {
  const location = useLocation();

  useEffect(() => {
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'page_view', {
        page_path: location.pathname + location.search,
      });
    }
  }, [location]);

  return null;
};

// Detects a Firebase email sign-in (magic) link on load and completes sign-in.
const MagicLinkHandler = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const handle = async () => {
      if (isEmailLinkSignIn()) {
        const user = await completeEmailLinkSignIn();
        if (user) {
          navigate("/dashboard", { replace: true });
        }
      }
    };
    handle();
  }, [navigate]);

  return null;
};

// Loading fallback component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

// Eagerly load critical pages (Landing, Login, Dashboard)
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";

// Lazy load all other pages
const WaitingPublic = lazy(() => import("./pages/WaitingPublic"));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail"));
const ThankYou = lazy(() => import("./pages/ThankYou"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const ContractReview = lazy(() => import("./pages/ContractReview"));
const ComparisonView = lazy(() => import("./pages/ComparisonView"));
const ContractSigning = lazy(() => import("./pages/ContractSigning"));
const IndividualWork = lazy(() => import("./pages/IndividualWork"));
const JointCollaboration = lazy(() => import("./pages/JointCollaboration"));
const WaitingList = lazy(() => import("./pages/WaitingList"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const RefundPolicy = lazy(() => import("./pages/RefundPolicy"));
const Journey = lazy(() => import("./pages/Journey"));
const Story = lazy(() => import("./pages/Story"));
const Contact = lazy(() => import("./pages/Contact"));
const Settings = lazy(() => import("./pages/Settings"));
const Profile = lazy(() => import("./pages/Profile"));
const Store = lazy(() => import("./pages/Store"));
const NotFound = lazy(() => import("./pages/NotFound"));
const RelationshipInsights = lazy(() => import("@/pages/RelationshipInsights"));
const HelpCenter = lazy(() => import("@/pages/help/HelpCenter"));
const HelpCategory = lazy(() => import("@/pages/help/HelpCategory"));
const HelpArticle = lazy(() => import("@/pages/help/HelpArticle"));
const FAQ = lazy(() => import("@/pages/help/FAQ"));
const InviteSignup = lazy(() => import("@/pages/InviteSignup"));
const JoinPartner = lazy(() => import("@/pages/JoinPartner"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const EmailUnsubscribe = lazy(() => import("./pages/EmailUnsubscribe"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const VersionHistory = lazy(() => import("./pages/VersionHistory"));

// Valentine's Day Collection
const ValentinePurchaseSuccess = lazy(() => import("./pages/ValentinePurchaseSuccess"));
const ValentineQuestions = lazy(() => import("./pages/ValentineQuestions"));
const ValentineLetter = lazy(() => import("./pages/ValentineLetter"));

// Preview Feature (Solo Preview)
const PreviewQuiz = lazy(() => import("./pages/preview/PreviewQuiz"));
const PreviewContract = lazy(() => import("./pages/preview/PreviewContract"));

const queryClient = new QueryClient();

const App = () => {
  // GA4 is initialized in index.html with Consent Mode v2
  // No need for react-ga4 initGA() — consent is managed via gtag directly

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <GoogleAnalyticsTracker />
          <MagicLinkHandler />
          <ErrorBoundary>
          <Suspense fallback={<PageLoader />}>
            <Routes>
          {/* Main route - Landing Page (dev branch) */}
          <Route path="/" element={<Landing />} />

          {/* Legal pages */}
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/cookie-policy" element={<CookiePolicy />} />
          <Route path="/refund-policy" element={<RefundPolicy />} />

          {/* Company/Product pages */}
          <Route path="/journey" element={<Journey />} />
          <Route path="/story" element={<Story />} />
          <Route path="/contact" element={<Contact />} />

          <Route path="/help" element={<HelpCenter />} />
          <Route path="/help/faq" element={<FAQ />} />
          <Route path="/help/:category" element={<HelpCategory />} />
          <Route path="/help/article/:id" element={<HelpArticle />} />

          {/* Waiting List (for pre-launch, available at /waiting) */}
          <Route path="/waiting" element={<WaitingList />} />

          {/* Archive routes (original)
          <Route path="/archive" element={<ArchiveLanding />} />
          <Route path="/archive/login" element={<ArchiveLogin />} />
          <Route path="/archive/signup" element={<ArchiveSignup />} />
          <Route path="/archive/dashboard" element={<ArchiveDashboard />} />
          <Route path="/archive/questionnaire" element={<ArchiveQuestionnaire />} />
          <Route path="/archive/contract" element={<ArchiveContract />} />
          */}

          {/* New Layouts - Basic Contract Generation Flow */}
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<WaitingPublic />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/thank-you" element={<ThankYou />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/onboarding" element={<Onboarding />} />
          <Route path="/contract-review" element={<ContractReview />} />
          <Route path="/relationship-insights" element={<RelationshipInsights />} />

          {/* Collaborative/Partner Flow */}
          <Route path="/comparison" element={<ComparisonView />} />
          <Route path="/contract-signing" element={<ContractSigning />} />
          <Route path="/individual-work" element={<IndividualWork />} />
          <Route path="/joint-collaboration" element={<JointCollaboration />} />

          {/* Admin Settings */}
          <Route path="/settings" element={<Settings />} />
          <Route path="/settings/:tab" element={<Settings />} />

          {/* User Profile */}
          <Route path="/profile" element={<Profile />} />

          {/* Store */}
          <Route path="/store" element={<Store />} />

          {/* Hidden invite signup (token-based) */}
          <Route path="/invite/:token" element={<InviteSignup />} />

          {/* FREEMIUM: Partner join link (pairing code in URL) */}
          <Route path="/join/:code" element={<JoinPartner />} />

          {/* Unsubscribe Routes */}
          <Route path="/unsubscribe" element={<Unsubscribe />} />
          <Route path="/email-unsubscribe" element={<EmailUnsubscribe />} />

          {/* Payment Routes */}
          <Route path="/payment-success" element={<PaymentSuccess />} />

          {/* Version History */}
          <Route path="/version-history" element={<VersionHistory />} />

          {/* Valentine's Day Collection */}
          <Route path="/valentine-success" element={<ValentinePurchaseSuccess />} />
          <Route path="/valentine-questions" element={<ValentineQuestions />} />
          <Route path="/valentine-letter" element={<ValentineLetter />} />

          {/* Preview Feature (Solo Preview) */}
          <Route path="/preview" element={<PreviewQuiz />} />
          <Route path="/preview/contract" element={<PreviewContract />} />

          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          </ErrorBoundary>
          <CookieBanner />
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
